import React, { memo, useEffect, useMemo, useState } from 'react';
import { Box, Button } from '~Styles';
import PageButton from './Components/PageButton';
import PrinterSettings from './Scenes/Printers';
import PosLayoutSettings from './Scenes/PosLayout';
import PaymentsSettings from './Scenes/Payments';
import KDSSettings from './Scenes/KDS';
import KitchenTicketsSettings from './Scenes/KitchenTickets';
import styled from '@emotion/styled';
import ZonesSettings from './Scenes/Zones';
import StationSettings from './Scenes/Stations';
import OtherSettings from './Scenes/Other';
import BackTopBar from '~ComponentsImproved/BackTopBar';
import Modal from '~ComponentsImproved/Modal';
import useModal from '~ComponentsImproved/Modal/useModal';
import Icon from '~ComponentsImproved/Icon';
import LanguageIcon from '~Assets/icons/language.svg';
import { useButton, useI18n, useLicenses, useLogger, useSelector, useStaffCan } from '~Hooks';
import { ModalContent } from '~Styles/modal';
import Heading from '~ComponentsImproved/Typography/Heading';
import { Languages } from '~Types';
import DeveloperPage from './Scenes/Developer';
import SafTPage from './Scenes/SafT';
import EjournalPage from './Scenes/Ejournal';
import { useHistory, useLocation } from 'react-router-dom';
import StaffSettings from './Scenes/StaffSettings';
import LoginModal from '~Scenes/Tables/Components/LoginModal';
import AppSettings from './App';
import BongMaster from '~Scenes/Tables/Components/BongMaster';
import ReceiptsSettings from './Scenes/Receipts';
import TablesSettings from './Scenes/Tables';

const Home = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  height: calc(100vh - 64px);
  background-color: var(--colorBlueGray1200);
  color: var(--colorWhite);
`;

const RightWrapper = styled.div`
  margin-right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
`;

const InnerWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const LanguageButton = styled.div<{ touching: boolean }>`
  margin-right: 8px;
  transform: ${({ touching }) => (touching ? 'scale(0.96)' : '1')};
`;

export const SettingsBlock = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
  background-color: var(--colorBlueGray1100);
  border-radius: 8px;
  border: 1px solid var(--colorBlueGray900);

  & > div {
    padding: 8px;
  }

  & > div:not(:last-child) {
    border-bottom: 1px solid var(--colorBlueGray900);
  }
`;

export const SettingsLoaderWrapper = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  padding: 48px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  overflow: hidden;
`;

const LanguageToggle = styled.div<{ active: boolean }>`
  height: 64px;
  padding: 8px;
  width: 144px;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border-radius: 4px;
  border: ${({ active }) =>
    active ? '2px solid var(--colorWhite)' : '2px solid var(--colorBlueGray900)'};
  cursor: pointer;
  transition: all 0.15s ease-out;
  font-weight: ${({ active }) => (active ? 600 : 400)};
  letter-spacing: 0.24px;
  font-size: 16px;
  color: ${({ active }) => (active ? 'var(--colorWhite)' : 'var(--colorBlueGray400)')};

  &:active {
    transform: scale(0.94);
  }
`;

const ScrollWrapper = styled.div`
  overflow-y: auto;
  height: 100%;
  flex: 1;
  margin-top: 4px;
`;

export const pages = [
  'printers',
  'kitchen-tickets',
  'KDS',
  'layout',
  'payments',
  'receipts',
  'zones',
  'tables',
  'station',
  'staff-settings',
  'app',
  'safT',
  'ejournal',
  'other',
] as const;

export type PageButtonType = typeof pages[number];
export type PageType = typeof pages[number] | 'developer';

const Settings = () => {
  const languageModal = useModal();
  const locale = useSelector((state) => state.locale);
  const [selectedLanguage, setSelectedLanguage] = useState<Languages>(locale);
  const country = useSelector((state) => state.country);
  const { changeLanguage, i18n } = useI18n();
  const logger = useLogger('settings-v2');
  const { pathname } = useLocation();
  const page = pathname.split('/').pop();
  const licenses = useLicenses();

  const staffId = useSelector((state) => state.staff?.id);
  const staffCan = useStaffCan();
  const venueId = useSelector((state) => state.venueId);
  const companyStaffs = useSelector((state) => state.companyStaff);

  const history = useHistory();

  useEffect(() => {
    setSelectedLanguage(locale);
  }, [locale]);

  const navigateToPage = (page: PageType | null) => {
    if (!page) {
      history.goBack();
      return;
    }

    history.push({
      pathname: `settings-v2/${page}`,
    });

    if (!page) {
      return;
    }

    logger.info(`Navigated to ${page}`);
  };

  const handleChangeLanguage = (loc: Languages) => {
    setSelectedLanguage(loc);
    changeLanguage(loc);
    logger.info(`Changed language to ${loc}`);
  };

  const PAGE_TITLES: Record<PageType, string> = {
    printers: i18n('Settings.Printers'),
    layout: i18n('Settings.PosLayout'),
    payments: i18n('Settings.Payments'),
    receipts: i18n('Settings.Receipts'),
    KDS: i18n('Settings.KdsSettings'),
    'kitchen-tickets': i18n('Settings.KitchenTickets'),
    zones: i18n('Settings.Zone'),
    tables: i18n('Settings.Tables'),
    station: i18n('Settings.Station'),
    safT: 'Saf-T',
    other: i18n('Settings.Other'),
    developer: 'Developer',
    ejournal: i18n('Settings.EJournal'),
    'staff-settings': i18n('Settings.StaffSettings'),
    app: i18n('Settings.AppSettings'),
  };

  const filteredPages = useMemo(() => {
    const hasStaff = companyStaffs && companyStaffs?.staffs?.length > 0;

    return pages.filter((page) => {
      if (page === 'app') return licenses.hasApp;
      if (page === 'staff-settings') return hasStaff;
      if (page === 'ejournal') return country === 'SE';
      if (page === 'safT') return country === 'NO';
      return true;
    });
  }, [pages, country, licenses.hasApp, companyStaffs?.staffs]);

  const pageToShow = useMemo(() => {
    switch (page) {
      case 'printers':
        return <PrinterSettings />;

      case 'layout':
        return (
          <InnerWrapper>
            <PosLayoutSettings />
          </InnerWrapper>
        );

      case 'payments':
        return (
          <InnerWrapper>
            <PaymentsSettings />
          </InnerWrapper>
        );

      case 'receipts':
        return (
          <InnerWrapper>
            <ReceiptsSettings />
          </InnerWrapper>
        );

      case 'KDS':
        return <KDSSettings />;

      case 'kitchen-tickets':
        return (
          <InnerWrapper>
            <KitchenTicketsSettings />
          </InnerWrapper>
        );

      case 'zones':
        return (
          <InnerWrapper>
            <ZonesSettings />
          </InnerWrapper>
        );

      case 'tables':
        return (
          <InnerWrapper>
            <TablesSettings />
          </InnerWrapper>
        );

      case 'station':
        return (
          <InnerWrapper>
            <StationSettings />
          </InnerWrapper>
        );

      case 'staff-settings':
        return (
          <InnerWrapper>
            <StaffSettings />
          </InnerWrapper>
        );

      case 'other':
        return (
          <InnerWrapper>
            <OtherSettings setSelectedPage={(value) => navigateToPage(value)} />
          </InnerWrapper>
        );

      case 'safT':
        return (
          <InnerWrapper>
            <SafTPage />
          </InnerWrapper>
        );

      case 'ejournal':
        return (
          <InnerWrapper>
            <EjournalPage />
          </InnerWrapper>
        );

      case 'app':
        return (
          <InnerWrapper>
            <AppSettings />
          </InnerWrapper>
        );

      case 'developer':
        return <DeveloperPage goBack={() => navigateToPage(null)} />;

      default:
        return (
          <Home>
            <Box padding="xl">
              <Box
                display="flex"
                gap="large"
                flexWrap="wrap"
                alignItems="center"
                justifyContent="center">
                {filteredPages.map((page) => (
                  <PageButton
                    key={page}
                    page={page}
                    setSelectedPage={(val) => navigateToPage(val)}
                  />
                ))}
              </Box>
              {/* TODO: hide until we have real questions */}
              {/* TODO: add logger for folding out of questions */}
              {/* <InnerWrapper>
                <FAQ />
              </InnerWrapper> */}
            </Box>
          </Home>
        );
    }
  }, [page, filteredPages]);

  const languageButton = useButton({
    onClick: languageModal.toggle,
  });

  const title =
    page && page in PAGE_TITLES ? PAGE_TITLES[page as PageType] : i18n('Settings.Title');

  return (
    <>
      <Wrapper>
        <BackTopBar title={title} goBack={() => history.goBack()} dark>
          <RightWrapper>
            <LanguageButton {...languageButton}>
              <Icon src={LanguageIcon} height="32px" width="32px" />
            </LanguageButton>
          </RightWrapper>
        </BackTopBar>
        <ScrollWrapper id="settings-scroll-wrapper">
          {/* If no staff show login modal, if the company don't use staff it will not show */}
          {!staffId && <LoginModal />}

          {staffCan.SETTINGS ? (
            pageToShow
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              flexDirection="column"
              alignItems="center"
              marginTop="large"
              padding="large">
              <Heading level={1} color="#fff" bottom={40}>
                {i18n('Settings.NoAccess')}
              </Heading>
              {staffCan.openMainMenu ? (
                <Button onClick={() => history.push(`/venue/${venueId}/gateway`)}>
                  {i18n('Settings.NoAccess.GoToGateway')}
                </Button>
              ) : (
                <Button onClick={() => history.push(`/venue/${venueId}/pos`)}>
                  {i18n('Settings.NoAccess.GoToPos')}
                </Button>
              )}
            </Box>
          )}
        </ScrollWrapper>
      </Wrapper>

      <Modal isShowing={languageModal.isShowing} hide={languageModal.toggle}>
        <ModalContent>
          <Box
            padding="xl"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center">
            <Heading level={1} color="#fff" bottom={40}>
              {i18n('Settings.SelectLanguage')}
            </Heading>
            <Box
              display="flex"
              gap="small"
              flexWrap="wrap"
              justifyContent="center"
              alignItems="center">
              <LanguageToggle
                onClick={() => handleChangeLanguage('en')}
                active={selectedLanguage === 'en'}>
                English
              </LanguageToggle>
              <LanguageToggle
                onClick={() => handleChangeLanguage('se')}
                active={selectedLanguage === 'se'}>
                Svenska
              </LanguageToggle>
              <LanguageToggle
                onClick={() => handleChangeLanguage('es')}
                active={selectedLanguage === 'es'}>
                Español
              </LanguageToggle>
              <LanguageToggle
                onClick={() => handleChangeLanguage('no')}
                active={selectedLanguage === 'no'}>
                Norsk
              </LanguageToggle>
              <LanguageToggle
                onClick={() => handleChangeLanguage('dk')}
                active={selectedLanguage === 'dk'}>
                Dansk
              </LanguageToggle>
              <LanguageToggle
                onClick={() => handleChangeLanguage('tr')}
                active={selectedLanguage === 'tr'}>
                Türkçe
              </LanguageToggle>
            </Box>
          </Box>
        </ModalContent>
      </Modal>

      {/* Bong master is here to be able to print auto accepted app orders */}
      <BongMaster />
    </>
  );
};

export default memo(Settings);
